// externals
import { connect } from 'react-redux';

// libraries
import { IShopper } from '@makemydeal/dr-platform-types';

// components
import { LeadFormUI } from './LeadFormUI';

// selectors
import { Selectors } from '../store';

// interfaces/types
import { IAdditionalSelectors } from '../types/IAdditionalSelectors';

// actions
import {
    onCloseLeadFormModal,
    onFirstNameBlur as onFirstNameBlurAction,
    onLastNameBlur as onLastNameBlurAction,
    onPhoneBlur as onPhoneBlurAction,
    onEmailBlur as onEmailBlurAction,
    onMessageBlur as onMessageBlurAction,
    onFormSubmit as onFormSubmitAction,
    onReinitializeForm as onReinitializeFormAction,
    Actions
} from '../store';
import { AnalyticsActions } from '../types/AnalyticsActions';

export const mapStateToProps = (state, props) => {
    const {
        selector,
        selector: { additionalSelectors }
    }: { selector: Selectors; additionalSelectors: IAdditionalSelectors } = props;
    const shopper: IShopper = additionalSelectors.getShopperInfo(state);
    const dealerZip = additionalSelectors.dealerSelectors.getDealerZip(state);
    const isTCPAEnabled = additionalSelectors.dealerSelectors.getTCPAEnabled(state);
    const isTCPACheckboxDisabled = additionalSelectors.dealerSelectors.getTCPACheckboxStatus(state);
    const isCCPAEnabled = additionalSelectors.dealerSelectors.getCCPAEnabled(state);
    const isPhoneNumberRequired = additionalSelectors.dealerSelectors.getPhoneNumberRequiredEnabled(state);
    const CCPAConsentText = additionalSelectors.dealerSelectors.getCCPAConsentText(state);
    const shopperConsentText = additionalSelectors.dealerSelectors.getShopperConsentText(state);
    const isPaymentTermsFallback = additionalSelectors.offerSelectors.isPaymentTermsFallback(state);
    const title = selector.getTitle(state);
    const description = selector.getDescription(state);
    const skipLink = selector.getSkipLink(state);
    const errorDescription = selector.getErrorDescription(state);
    const externalErrorRetries = selector.getExternalErrorCount(state);
    const successAction = selector.getSuccessAction(state);
    const leadFormSubmitting = selector.isLeadFormSubmitting(state);
    const leadFormSubmittedSuccess = selector.isLeadFormSubmittedSuccess(state);
    const leadFormSubmittedFailure = selector.isLeadFormSubmittedFailure(state);
    const leadFormReinitialized = selector.isLeadFormReinitialized(state);
    const staticImages = props.staticImages;
    const preSubmitAction = selector.getPreSubmitAction(state);
    const zipCodeLoading = selector.getZipCodeLoading(state);
    const leadFormCCPAShown = selector.isLeadFormCCPAShown(state);
    const isTradePaymentFailed = additionalSelectors.tradeInSelectors.isTradePaymentFailed(state);
    const dealerPhoneNumber = additionalSelectors.dealerSelectors.getDealerPhoneNumber(state);

    return {
        shopper,
        leadFormSubmitting,
        leadFormSubmittedSuccess,
        leadFormSubmittedFailure,
        leadFormReinitialized,
        isLeadFormOptional: skipLink,
        shopperConsentText,
        isTCPAEnabled,
        isTCPACheckboxDisabled,
        isCCPAEnabled,
        CCPAConsentText,
        dealerZip,
        title,
        description,
        errorDescription,
        successAction,
        staticImages,
        externalErrorRetries,
        preSubmitAction,
        isPhoneNumberRequired,
        zipCodeLoading,
        leadFormCCPAShown,
        isPaymentTermsFallback,
        isTradePaymentFailed,
        dealerPhoneNumber
    };
};

export const mapDispatchToProps = (dispatch, props) => {
    const { actionCreators, dispatchAnalytics } = props;
    const updateShopper = (shopper: IShopper) => {
        dispatch(
            actionCreators.updatedShopper({
                shopper,
                message: shopper.message || ''
            })
        );
    };

    const onSubmit = (shopper: IShopper) => {
        dispatch(
            actionCreators.updatedShopper({
                shopper,
                message: shopper.message || ''
            })
        );
        dispatch(onFormSubmitAction());
        dispatch(actionCreators.updateCommunicationPreference());
        actionCreators.checkMessageOnFormSubmit(shopper, dispatch);
        dispatch(actionCreators.checkZipOnFormSubmit(shopper.zip));
        dispatch(actionCreators.submitOffer(true));
    };

    const onReinitializeForm = () => {
        dispatch(onReinitializeFormAction());
    };

    const dispatchPreSubmitEvent = (preSubmitEvent) => {
        dispatch({
            type: preSubmitEvent
        });
    };

    const onLeadFormSubmittedSuccess = (successAction) => {
        if (successAction) {
            dispatch(onFormSubmitAction());
            dispatch({ type: successAction });
        } else {
            dispatch(onCloseLeadFormModal());
        }
    };

    const onFirstNameBlur = (shopper: IShopper) => {
        dispatch(onFirstNameBlurAction());
        updateShopper(shopper);
    };
    const onLastNameBlur = (shopper: IShopper) => {
        dispatch(onLastNameBlurAction());
        updateShopper(shopper);
    };
    const onPhoneBlur = (shopper: IShopper) => {
        dispatch(onPhoneBlurAction());
        updateShopper(shopper);
    };
    const onEmailBlur = (shopper: IShopper) => {
        dispatch(onEmailBlurAction());
        updateShopper(shopper);
    };
    const onMessageBlur = (shopper: IShopper) => {
        dispatch(onMessageBlurAction());
        updateShopper(shopper);
    };

    const routeWithoutSubmitting = () => {
        dispatch(onCloseLeadFormModal());
    };

    const backToDashboard = () => {
        dispatch(actionCreators.goToNext('/dashboard'));
        dispatch(onCloseLeadFormModal());
        dispatch(dispatchAnalytics(AnalyticsActions.DR_LEADFORM_SKIPPED, 'Lead form skipped on [CURRENTROUTE]'));
    };
    const dismissPaymentTermsFallbackMessage = () => {
        dispatch(actionCreators.hidePaymentTermsFallbackMessage());
    };

    const removeTradeInFromSummary = () => {
        dispatch({ type: Actions.SUMMARY_REMOVE_TRADE_IN_CLICKED });
        dispatch(actionCreators.goToNext('/dashboard'));
        dispatch(onCloseLeadFormModal());
    };

    return {
        updateShopper,
        onSubmit,
        onLeadFormSubmittedSuccess,
        onFirstNameBlur,
        onLastNameBlur,
        onPhoneBlur,
        onEmailBlur,
        onMessageBlur,
        routeWithoutSubmitting,
        backToDashboard,
        onReinitializeForm,
        dispatchPreSubmitEvent,
        dismissPaymentTermsFallbackMessage,
        removeTradeInFromSummary
    };
};

export const LeadForm = connect(mapStateToProps, mapDispatchToProps)(LeadFormUI);

export default LeadForm;
